import {
  getAllIds,
  getCountByStatus,
  Tirage,
  useGetTiragesByArtwork,
} from "@/api/tirages/tirage.api";
import { useAddToTrash } from "@/api/trash";
import ShowElements from "@/components/drawer/ShowElements";
import NewItemDialogTrigger from "@/components/modal/NewItemDialogTrigger";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import StatusTableTab from "@/components/StatusTableTab";
import { AdvancedSearchChip } from "@/components/table/AdvancedSearchChip";
import { AdvancedSort } from "@/components/table/AdvancedSort";
import { GlobalSearch } from "@/components/table/GlobalSearch";
import { CheckboxGroupMask } from "@/components/form-items/CheckboxGroupMask";
import DuplicateTirageDialog from "@/components/tirage/DuplicateTirageDialog";
import NewTirageDialog from "@/components/tirage/NewTirageDialog";
import TirageSheetForm from "@/components/tirage/TirageSheetForm";
import {
  TableRowActionsMenu,
  type TableRowAction,
} from "@/components/table/TableRowActionsMenu";
import { ACTION_I18N_KEYS } from "@/components/table/action-i18n-keys";
import {
  ACTIONS_COLUMN_ID,
  createActionsColumnDef,
} from "@/components/table/table-actions-column";
import { DataTable } from "@/components/ui/datatable";
import { ColumnFormatted, generateTable } from "@/config/dashboard/general";
import {
  tiragesAdvancedSearchConfig,
  tiragesColumns,
} from "@/config/dashboard/tirages";
import { useAuth } from "@/context/auth/AuthContext";
import { toast } from "@/hooks/use-toast";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { ACTION_VIEW_ICON } from "@/components/table/action-icons";
import { Copy, Pencil, Trash2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const STORAGE_KEY = "tirage-table-preferences";

const flatVisibleTirageColumns = [
  ...tiragesColumns.flatMap((column: ColumnFormatted) =>
    column.visible ? column.key : []
  ),
  ACTIONS_COLUMN_ID,
];

const TirageList = ({ artworkId }: { artworkId: string }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isReader = user?.role === "reader";
  const trashAdder = useAddToTrash();

  const {
    pagination,
    setPagination,
    countByStatus,
    currentCount,
    fetchParams,
    filterStatus,
    setFilterStatus,
  } = useDashboardParams<Tirage>({
    resourceName: "tirage",
    resourceCounter: (where) => getCountByStatus(artworkId, where),
    statusField: "tiragePublicationStatus",
  });

  const isFirstChild = useMemo(
    () =>
      ((countByStatus?.published as number) ?? 0) +
        ((countByStatus?.draft as number) ?? 0) ===
      0,
    [countByStatus],
  );

  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedTirageId, setSelectedTirageId] = useState<
    string | undefined
  >();
  const [initialTirageData, setInitialTirageData] = useState<
    Partial<Tirage> | undefined
  >();
  const [duplicateSourceTirageId, setDuplicateSourceTirageId] = useState<
    string | undefined
  >();
  const [previewTirageId, setPreviewTirageId] = useState<string | undefined>();
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleOpenTirageSheet = (tirage: { id: string; tirageDescriptionNumeroEnregistrement?: string }) => {
    setSelectedTirageId(tirage.id);
    setInitialTirageData({
      tirageDescriptionNumeroEnregistrement: tirage.tirageDescriptionNumeroEnregistrement,
    });
    setSheetOpen(true);
  };

  const handleAddToTrash = useCallback(
    (tirage: Tirage) => {
      if (tirage.tiragePublicationStatus === "bin") {
        toast({
          title: t("documentAlreadyInTrashToastTitle"),
          variant: "success",
          description: t("documentAlreadyInTrashToastDescription"),
        });
        return;
      }

      trashAdder.mutate(
        { documentId: tirage.id, documentType: "tirage" },
        {
          onSuccess: () => {
            toast({
              title: t("documentAddedToTrashToastTitle"),
              variant: "success",
              description: t("documentAddedToTrashToastDescription"),
            });
          },
        },
      );
    },
    [t, trashAdder],
  );

  const defaultOrder = useMemo(
    () => [{ tirageDescriptionNumeroEnregistrement: 'asc' as const }],
    [],
  );

  const orderWithDefault = useMemo(
    () => fetchParams.order && fetchParams.order.length > 0
      ? fetchParams.order
      : defaultOrder,
    [fetchParams.order, defaultOrder],
  );

  const { data } = useGetTiragesByArtwork({
    artworkId: artworkId,
    take: fetchParams.take,
    skip: fetchParams.skip,
    where: fetchParams.where,
    order: orderWithDefault,
  });

  const previewTirage = useMemo(
    () => data?.find((item) => item.id === previewTirageId),
    [data, previewTirageId],
  );

  const columns: ColumnDef<Tirage, any>[] = useMemo(() => {
    const baseColumns = generateTable(tiragesColumns, "tirage");
    return [
      ...baseColumns,
      createActionsColumnDef<Tirage>(({ row }) => {
        const tirage = row.original;
        const actions: TableRowAction[] = [
          {
            id: "edit",
            label: t(ACTION_I18N_KEYS.edit),
            icon: Pencil,
            onSelect: () => {
              setSelectedTirageId(tirage.id);
              setInitialTirageData(undefined);
              setSheetOpen(true);
            },
          },
          {
            id: "duplicate",
            label: t(ACTION_I18N_KEYS.duplicate),
            icon: Copy,
            onSelect: () => setDuplicateSourceTirageId(tirage.id),
          },
          {
            id: "view",
            label: t(ACTION_I18N_KEYS.view),
            icon: ACTION_VIEW_ICON,
            onSelect: () => {
              setPreviewTirageId(tirage.id);
              setPreviewOpen(true);
            },
          },
          {
            id: "delete",
            label: t(ACTION_I18N_KEYS.delete),
            icon: Trash2,
            destructive: true,
            hidden: isReader,
            disabled:
              trashAdder.isPending || tirage.tiragePublicationStatus === "bin",
            onSelect: () => handleAddToTrash(tirage),
          },
        ];

        return (
          <div
            className="flex justify-end"
            onClick={(event) => event.stopPropagation()}
          >
            <TableRowActionsMenu actions={actions} />
          </div>
        );
      }),
    ];
  }, [t, isReader, trashAdder.isPending, handleAddToTrash]);

  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    const saved = readTablePreferences(STORAGE_KEY);
    if (saved) {
      const { order } = saved;
      return order || flatVisibleTirageColumns;
    }
    return flatVisibleTirageColumns;
  });

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const saved = readTablePreferences(STORAGE_KEY);
      if (saved) {
        const { visibility } = saved;
        return (
          visibility || {
            ...tiragesColumns.reduce(
              (acc, column) => ({
                ...acc,
                [column.key]: column.visible || false,
              }),
              {},
            ),
          }
        );
      }
      return {
        ...tiragesColumns.reduce(
          (acc, column) => ({
            ...acc,
            [column.key]: column.visible || false,
          }),
          {}
        ),
        [ACTIONS_COLUMN_ID]: true,
      };
    },
  );

  const [selectionState, setSelectionState] = useState({});

  return (
    <CheckboxGroupMask
      groupName="artworkTirageListTableGroup"
      label={t("dashboardTitleTirages")}
      className="w-full"
    >
    <div className="flex flex-col items-start gap-3 w-full">
      <div className="flex justify-between  items-center w-full">
        <GlobalSearch config={tiragesAdvancedSearchConfig} data={data ?? []} />
      </div>
      <div className="flex flex-row justify-between w-full">
        <StatusTableTab
          filterStatus={filterStatus}
          countByStatus={countByStatus}
          onValueChange={setFilterStatus}
        />
        <div className="flex flex-row gap-2">
          <AdvancedSort config={tiragesAdvancedSearchConfig} />
          <NewItemDialogTrigger
            title={t("createTirage")}
            Dialog={(props) => (
              <NewTirageDialog
                {...props}
                artworkId={artworkId}
                isFirstChild={isFirstChild}
                onCreateTirage={handleOpenTirageSheet}
              />
            )}
          />
        </div>
      </div>
      <div className="w-full flex flex-row">
        <AdvancedSearchChip />
      </div>
      <div className="w-full">
        <DataTable
          onRowClick={(row) => {
            setSelectedTirageId(row.original.id);
            setInitialTirageData(undefined);
            setSheetOpen(true);
          }}
          pagination={{
            pagination: pagination,
            setPage: setPagination,
            totalSize: currentCount,
          }}
          columns={columns}
          data={data ?? []}
          storageKey={STORAGE_KEY}
          options={{
            enableColumnResizing: true,
            state: {
              columnVisibility: columnVisibility,
              columnOrder: columnsOrder,
            },
            onColumnVisibilityChange: setColumnVisibility,
            onColumnOrderChange: setColumnsOrder,
          }}
          selection={{
            getRowId: (p) => p.id,
            getAllRowIds: () => getAllIds(artworkId, fetchParams.where),
            state: {
              selectionState,
              setSelectionState,
            },
            enableSelectAll: true,
          }}
        />
      </div>
      {selectedTirageId && (
        <TirageSheetForm
          open={sheetOpen}
          onOpenChange={(open) => {
            setSheetOpen(open);
            if (!open) {
              setSelectedTirageId(undefined);
              setInitialTirageData(undefined);
            }
          }}
          tirageId={selectedTirageId}
          artworkId={artworkId}
          initialData={initialTirageData}
        />
      )}
      <DuplicateTirageDialog
        open={!!duplicateSourceTirageId}
        onOpenChange={(open) => {
          if (!open) setDuplicateSourceTirageId(undefined);
        }}
        sourceTirageId={duplicateSourceTirageId}
        artworkId={artworkId}
        onDuplicateSuccess={(created) => {
          setDuplicateSourceTirageId(undefined);
          handleOpenTirageSheet(created);
        }}
      />
      {previewTirage && (
        <ShowElements
          id={previewTirage.id}
          type="tirage"
          open={previewOpen}
          onOpenChange={(open) => {
            setPreviewOpen(open);
            if (!open) setPreviewTirageId(undefined);
          }}
          elements={[
            {
              label: t("tirageDescriptionNumeroEnregistrement"),
              value: previewTirage.tirageDescriptionNumeroEnregistrement ?? "",
              type: "text",
            },
            {
              label: t("tirageDescriptionNotes"),
              value: previewTirage.tirageDescriptionNotes ?? "",
              type: "richtext",
            },
            {
              label: t("attachments"),
              value: [],
              type: "image",
            },
          ]}
        />
      )}
    </div>
    </CheckboxGroupMask>
  );
};

export default TirageList;
